import React, {
  type FormEvent,
  useState,
  type ReactNode,
  type Ref,
  useImperativeHandle,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { FORM_VALIDATION_ERROR } from 'src/app/shared/constants/errorMessages';
import { formStatus } from 'src/app/shared/constants/formStatus';
import {
  DEFAULT_TIMEOUT_JOB_SAVING,
  DEFAULT_TIMEOUT_JOB_SAVED,
} from 'src/app/shared/constants/timeouts';
import parseValidationResult from 'src/app/utils/parseValidationResult';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { useDraft } from 'src/app/v1/views/hooks/useDraft';
import type { AppState } from 'src/types/appState';
import type {
  ValidationComponent,
  ValidationResultType,
} from 'src/types/validation';

export interface FormProps extends Partial<ValidationComponent> {
  children?: ReactNode;
  handleNextPageRoute: () => void;
  /**
   * handlePreSubmit
   * Perform a pre-submit check before the actual submit logic
   * @returns {boolean} - Returns `true` to stop the submission process; or `false` or not passing in to continue with submission
   */
  handlePreSubmit?: () => boolean;
}

export interface FormRef {
  onValidationSuccess: () => void;
}

export const Form = React.forwardRef((props: FormProps, ref: Ref<FormRef>) => {
  const [status, setStatus] = useState(formStatus.EDIT);
  const isEdit = status === formStatus.EDIT;

  const { tealiumPage } = useSelector(
    (state: AppState) => ({
      tealiumPage: state.tealium.page,
    }),
    shallowEqual,
  );

  const { saveDraft } = useDraft();

  const handleSavedState = () => {
    setStatus(formStatus.SAVED);
    setTimeout(() => {
      props.handleNextPageRoute();
    }, DEFAULT_TIMEOUT_JOB_SAVED);
  };

  const onValidationSuccess = () => {
    if (isEdit) {
      saveDraft();
      setStatus(formStatus.SAVING);
      setTimeout(() => {
        handleSavedState();
      }, DEFAULT_TIMEOUT_JOB_SAVING);

      const eventPageName = tealiumPage.replace('-', '_');

      const eventData = {
        eventName: `${eventPageName}${'_continue_pressed'}`,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };

  const handleValidationResult = (
    validationResult: ValidationResultType,
  ): void => {
    const { isValid, errorMessages } = parseValidationResult(validationResult);

    if (!isValid) {
      const eventData = {
        eventName: FORM_VALIDATION_ERROR,
        errorMessage: errorMessages.join(','),
        validationType: 'form',
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      if (props.handlePreSubmit?.() === true) {
        return;
      }
      onValidationSuccess();
    }
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const { handleValidation } = props;

    if (isEdit) {
      const validationResult = !handleValidation || (await handleValidation());
      return handleValidationResult(validationResult);
    }
  };

  useImperativeHandle(ref, () => ({ onValidationSuccess }));

  return <form onSubmit={handleSubmit}>{props.children}</form>;
});
